import { DOCUMENT } from '@angular/common'
import { Inject, Injectable, OnDestroy } from '@angular/core'
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router'
import { Subscription } from 'rxjs'

/** Id do preloader estático do `index.html`, na tela desde o primeiro paint — antes do Angular. */
export const APP_PRELOADER_ID = 'app-preloader'

/** Classe que esmaece o preloader; o elemento sai do DOM ao fim da transição. */
export const APP_PRELOADER_DONE_CLASS = 'app-preloader--done'

/** Classe do estado de falha: sem giro, com a mensagem de erro e o botão "Recarregar". */
export const APP_PRELOADER_ERROR_CLASS = 'app-preloader--error'

/** Duração da transição de saída definida no `index.html`, com folga. */
const FADE_OUT_MS = 400

/**
 * Falha antes da primeira tela (configuração em tempo de execução, sessão ou bootstrap do Angular):
 * o preloader avisa e oferece recarregar, em vez de girar para sempre. É DOM puro de propósito — roda
 * no `main.ts`, quando a injeção de dependência do Angular pode nem existir.
 */
export function showAppPreloaderError(document: Document): void {
	const preloader = document.getElementById(APP_PRELOADER_ID)
	if (!preloader) return

	preloader.classList.add(APP_PRELOADER_ERROR_CLASS)
	preloader.setAttribute('aria-busy', 'false')

	const message = preloader.querySelector('[data-app-preloader-message]')
	if (message) message.textContent = 'Não foi possível carregar o SilGov. Verifique sua conexão e tente novamente.'
}

/**
 * Preloader central do carregamento inicial do SPA (27/09/2026).
 *
 * O `index.html` já nasce com ele na tela e cobre o que acontece antes de o Angular montar a raiz: o
 * download do bundle, a configuração em tempo de execução e a renovação silenciosa da sessão
 * (`APP_INITIALIZER`). Este serviço o retira quando a PRIMEIRA navegação assenta — guards (entre eles o
 * contexto de permissões, buscado no servidor) e módulo sob demanda carregados, primeira tela montada.
 * Daí em diante cada tela usa o próprio `app-pre-loading`.
 *
 * "Assentar" é a navegação terminar sem que outra comece em seguida: um guard que redireciona (sem
 * sessão, sem cliente selecionado) cancela a navegação e abre outra no mesmo ciclo, e só a última conta.
 */
@Injectable({ providedIn: 'root' })
export class AppPreloaderService implements OnDestroy {
	/** Rede muito lenta ou navegação que não assenta: a tela nunca fica presa atrás do preloader. */
	static readonly SAFETY_TIMEOUT_MS = 20_000

	private finished = false
	private subscription?: Subscription
	private safetyTimeout?: ReturnType<typeof setTimeout>

	constructor(
		private router: Router,
		@Inject(DOCUMENT) private document: Document
	) {}

	/** Retira o preloader quando a navegação inicial assentar, ou no tempo-limite. Chamar uma vez, no boot. */
	finishAfterInitialNavigation(): void {
		if (this.finished || this.subscription) return

		this.safetyTimeout = setTimeout(() => this.finish(), AppPreloaderService.SAFETY_TIMEOUT_MS)

		let lastStartedId = 0

		this.subscription = this.router.events.subscribe(event => {
			if (event instanceof NavigationStart) {
				lastStartedId = event.id
				return
			}

			if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
				const settledId = event.id

				// Um tique depois: se um redirecionamento abriu outra navegação, ela é a que vale.
				setTimeout(() => {
					if (lastStartedId === settledId) this.finish()
				})
			}
		})
	}

	/** Esmaece e remove o preloader. Idempotente. */
	finish(): void {
		if (this.finished) return
		this.finished = true
		this.dispose()

		const preloader = this.document.getElementById(APP_PRELOADER_ID)
		if (!preloader) return

		preloader.classList.add(APP_PRELOADER_DONE_CLASS)
		preloader.setAttribute('aria-busy', 'false')
		setTimeout(() => preloader.remove(), FADE_OUT_MS)
	}

	ngOnDestroy(): void {
		this.dispose()
	}

	private dispose(): void {
		this.subscription?.unsubscribe()
		this.subscription = undefined

		if (this.safetyTimeout) {
			clearTimeout(this.safetyTimeout)
			this.safetyTimeout = undefined
		}
	}
}
